/**
 * Data sources for the voice-subscription onboarding-demo screens.
 *
 * Live queue depth has no backend endpoint yet (likely a different service
 * than pricing-svc), so it stays hardcoded below.
 *
 * Plan sizing, billing action log, not-charged summary, trial SMS/CRM counts,
 * and alert preferences are real pricing-svc calls (see voiceSubscriptionApi.ts).
 */

import {
  getVoiceBillingActionLog,
  getVoiceNotChargedSummary,
  getVoiceTrialActivity,
  getVoiceAlertPreferences,
  getVoiceSizing,
  putVoiceSizing,
} from '@/services/voiceSubscriptionApi';
import type { VoiceSizingRequest } from '@/types/voice-subscription';

export interface QueueStatus {
  totalWaiting: number;
  handled: number;
  remaining: number;
  recentBookings: number;
  recentBookingWindowMinutes: number;
}

export interface PlanSizingBenchmarks {
  audiencePerMonth: number;
  responseRatePct: number;
  bookPct: number;
  transferPct: number;
  isSaved: boolean;
  /** Not returned by /api/voice/sizing — filled from trial usage when available. */
  avgSessionMinutes: number | null;
}

export interface TrialActivityExtras {
  smsConfirmations: number;
  crmRecordsWritten: number;
  recipientMemoryCount: number;
  totalMinutes: number;
}

export interface BillingActionLogEntry {
  time: string;
  recipientLabel: string;
  action: string;
  eventType: string;
  feeUsd: number;
}

export interface NotChargedItem {
  label: string;
  detail: string;
}

export interface AlertPreference {
  id: string;
  enabled: boolean;
  threshold: string | null;
}

// TODO(voice-backend): replace with GET /api/voice/ops/queue/{tenantId}
export async function fetchQueueStatus(tenantId: number): Promise<QueueStatus> {
  return {
    totalWaiting: 63,
    handled: 41,
    remaining: 22,
    recentBookings: 6,
    recentBookingWindowMinutes: 38,
  };
}

export async function fetchPlanSizingBenchmarks(tenantId: number): Promise<PlanSizingBenchmarks> {
  try {
    const res = await getVoiceSizing(tenantId);
    return {
      audiencePerMonth: res.audiencePerMonth ?? 2000,
      responseRatePct: res.responseRatePct ?? 5.6,
      bookPct: res.bookPct ?? 36,
      transferPct: res.transferPct ?? 0,
      isSaved: res.isSaved ?? false,
      avgSessionMinutes: null,
    };
  } catch {
    return {
      audiencePerMonth: 2000,
      responseRatePct: 5.6,
      bookPct: 36,
      transferPct: 0,
      isSaved: false,
      avgSessionMinutes: null,
    };
  }
}

export async function savePlanSizing(
  tenantId: number,
  payload: VoiceSizingRequest
): Promise<PlanSizingBenchmarks> {
  const res = await putVoiceSizing(tenantId, payload);
  return {
    audiencePerMonth: res.audiencePerMonth,
    responseRatePct: res.responseRatePct,
    bookPct: res.bookPct,
    transferPct: res.transferPct,
    isSaved: res.isSaved,
    avgSessionMinutes: null,
  };
}

// recipientMemoryCount has no pricing-svc endpoint (likely owned by the
// agent/session service), so it stays hardcoded. totalMinutes is available
// for real via useVoiceUsageSummary (total_minutes_used) — components should
// prefer that over this field.
export async function fetchTrialActivityExtras(tenantId: number): Promise<TrialActivityExtras> {
  try {
    const res = await getVoiceTrialActivity(tenantId);
    return {
      smsConfirmations: res.counts?.sms_delivered ?? 0,
      crmRecordsWritten: res.counts?.crm_write_confirmed ?? 0,
      recipientMemoryCount: 41,
      totalMinutes: 162,
    };
  } catch {
    return {
      smsConfirmations: 0,
      crmRecordsWritten: 0,
      recipientMemoryCount: 0,
      totalMinutes: 0,
    };
  }
}

export async function fetchBillingActionLog(
  tenantId: number,
  billingPeriod: string
): Promise<BillingActionLogEntry[]> {
  try {
    const res = await getVoiceBillingActionLog(tenantId, billingPeriod);
    return (res.entries ?? []).map((e) => ({
      time: new Date(e.occurred_at).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }),
      recipientLabel: e.recipient_label,
      action: e.action_description,
      eventType: e.event_type,
      feeUsd: e.fee_usd,
    }));
  } catch {
    return [];
  }
}

export async function fetchNotChargedSummary(
  tenantId: number,
  billingPeriod: string
): Promise<NotChargedItem[]> {
  try {
    const res = await getVoiceNotChargedSummary(tenantId, billingPeriod);
    return (res.items ?? []).map((item) => ({ label: item.label, detail: item.detail }));
  } catch {
    return [];
  }
}

export async function fetchAlertPreferences(tenantId: number): Promise<AlertPreference[]> {
  const res = await getVoiceAlertPreferences(tenantId);
  return res.alerts.map((a) => ({ id: a.alert_id, enabled: a.enabled, threshold: a.threshold }));
}
